<template>
  <div>
    <div class="main-container">
      <!-- ===== Breadcrumb ===== -->
      <div aria-label="Breadcrumb" class="breadcrumb-container">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <RouterLink to="/settings" custom v-slot="{ navigate }">
              <a href="#" @click.prevent="onClickBreadcrumb(navigate)"> 設定トップ </a>
            </RouterLink>
            <span class="breadcrumb-separator"></span>
          </li>
          <li class="breadcrumb-item">
            <RouterLink to="/flight-routes" custom v-slot="{ navigate }">
              <a href="#" @click.prevent="onClickBreadcrumb(navigate)"> 航路選択 </a>
            </RouterLink>
            <span class="breadcrumb-separator"></span>
          </li>
          <li class="breadcrumb-item">
            <span aria-current="page">航路情報設定</span>
          </li>
        </ol>
      </div>

      <!-- ===== Main Container ===== -->
      <Form v-slot="{ meta, errors }" @submit="handleSave">
        <div class="container">
          <div class="advanced-container">
            <!-- ===== 航路名 ===== -->
            <div class="spot-name-edit">
              <label class="label-l">航路名</label>
              <InputText
                name="routeName"
                v-model="route.flight_route_name"
                :rules="isRouteRequired ? 'required|max_length:20' : ''"
                :maxlength="20"
              />
              <ErrorMessageBox name="routeName" />
            </div>

            <!-- ===== 地点登録 ===== -->
            <div class="spot-ad-edit">
              <p class="spot-ad-edit-title">
                地点登録
                <span>※地点は最大30か所まで登録可能です</span>
              </p>

              <div class="spot-ad-edit-note">
                <ul>
                  <li><span></span>航路上の風</li>
                  <li><span></span>上空の風</li>
                </ul>
                <p>
                  ※『航路上の風』は最大10カ所まで設定可能です<br />
                  ※『上空の風』は最大3カ所まで設定可能です
                </p>
              </div>

              <!-- ===== 地点リスト ===== -->
              <draggable
                v-model="route.points"
                item-key="id"
                handle=".drag-handle"
                class="route-points-list"
                :force-fallback="true"
                :fallback-on-body="true"
                :swap-threshold="0.65"
                :scroll-sensitivity="100"
              >
                <template #item="{ element, index }">
                  <li
                    class="route-point-item"
                    :class="{ 'has-error': hasPointError(element.id, errors) }"
                  >
                    <details class="advanced-settings-details" :open="element.opened" role="group">
                      <summary
                        class="details-summary accordion-header"
                        @click.prevent="togglePoint(element)"
                      >
                        <div class="details-summary-title">
                          <button class="drag-handle"></button>

                          <div class="summary-number">{{ index + 1 }}</div>

                          <div class="summary-info-container">
                            <div class="summary-title-row">
                              <!-- 発 -->
                              <img
                                v-if="index === 0"
                                src="../assets/images/icon/icon_route_start.svg"
                                alt="発"
                                class="summary-header-icon"
                              />

                              <!-- 着 -->
                              <img
                                v-else-if="index === route.points.length - 1"
                                src="../assets/images/icon/icon_route_goal.svg"
                                alt="着"
                                class="summary-header-icon"
                              />

                              <div class="summary-text">
                                {{ element.point_name || '地点' }}
                              </div>
                            </div>

                            <ul class="summary-meta-list">
                              <li class="summary-meta-height">
                                {{ element.altitude }}<span>m</span>
                              </li>
                              <li v-if="element.route_layer" class="summary-meta-icon">
                                <img src="../assets/images/icon/icon_pin.svg" alt="航路上の風" />
                              </li>
                              <li v-if="element.upper_layer" class="summary-meta-icon">
                                <img src="../assets/images/icon/icon_flag.svg" alt="上空の風" />
                              </li>
                            </ul>
                          </div>
                        </div>
                        <span class="icon"></span>
                      </summary>

                      <!-- ===== 詳細 ===== -->
                      <div class="details-content">
                        <div class="details-inner-content">
                          <ul>
                            <!-- 地点名 -->
                            <li class="spot-name-input">
                              <div>
                                <label class="label-l">
                                  地点名 <span class="required-mark">※必須</span>
                                </label>
                                <InputText
                                  :name="`points.${element.id}.point_name`"
                                  label="地点名"
                                  v-model="element.point_name"
                                  :rules="
                                    isPointValidationActive(element, element.id)
                                      ? 'required|max_length:20'
                                      : ''
                                  "
                                  @blur="touchedPoints[element.id] = true"
                                />
                                <ErrorMessageBox :name="`points.${element.id}.point_name`" />
                              </div>
                            </li>

                            <!-- 高度 / 速度 -->
                            <li class="spot-h-s">
                              <div>
                                <label class="label-l"
                                  >高度<span class="required-mark">※必須</span></label
                                >
                                <NumberField
                                  :name="`points.${element.id}.altitude`"
                                  label="高度"
                                  v-model="element.altitude"
                                  :rules="
                                    isPointValidationActive(element, element.id)
                                      ? 'required|between:0,200'
                                      : ''
                                  "
                                  :decimals="0"
                                  @blur="touchedPoints[element.id] = true"
                                />
                                <span class="input-unit">m</span>
                                <ErrorMessageBox :name="`points.${element.id}.altitude`" />
                              </div>
                              <div>
                                <label class="label-l"
                                  >飛行速度<span class="required-mark">※必須</span></label
                                >
                                <NumberField
                                  :name="`points.${element.id}.speed`"
                                  label="飛行速度"
                                  v-model="element.speed"
                                  :rules="
                                    isPointValidationActive(element, element.id)
                                      ? 'required|between:0,999.99,2'
                                      : ''
                                  "
                                  @blur="touchedPoints[element.id] = true"
                                  :decimals="2"
                                  inputClass="speed-input"
                                />
                                <span class="input-unit">m/s</span>
                                <ErrorMessageBox :name="`points.${element.id}.speed`" />
                              </div>
                            </li>

                            <!-- 緯度 / 経度 -->
                            <li class="spot-lat-lon">
                              <div>
                                <label class="label-l"
                                  >緯度<span class="required-mark">※必須</span></label
                                >
                                <NumberField
                                  :name="`points.${element.id}.lat`"
                                  label="緯度"
                                  v-model="element.lat"
                                  :rules="
                                    isPointValidationActive(element, element.id)
                                      ? `required|between:${MIN_LATITUDE},${MAX_LATITUDE},7`
                                      : ''
                                  "
                                  @blur="touchedPoints[element.id] = true"
                                  :decimals="7"
                                />
                                <ErrorMessageBox :name="`points.${element.id}.lat`" />
                              </div>
                              <div>
                                <label class="label-l"
                                  >経度<span class="required-mark">※必須</span></label
                                >
                                <NumberField
                                  :name="`points.${element.id}.lon`"
                                  label="経度"
                                  v-model="element.lon"
                                  :rules="
                                    isPointValidationActive(element, element.id)
                                      ? `required|between:${MIN_LONGITUDE},${MAX_LONGITUDE},7`
                                      : ''
                                  "
                                  @blur="touchedPoints[element.id] = true"
                                  :decimals="7"
                                />
                                <ErrorMessageBox :name="`points.${element.id}.lon`" />
                              </div>
                              <button
                                class="button-secondary"
                                @click.prevent="openMapDialog(element, index)"
                              >
                                <span>
                                  <img src="../assets/images/icon/icon_file_map_stack.svg" alt="" />
                                </span>
                                地図から緯度/経度を修正する
                              </button>
                            </li>

                            <!-- 風設定 -->
                            <li class="spot-wind-option">
                              <fieldset class="fieldset-group">
                                <legend class="fieldset-legend">航路情報</legend>
                                <div class="toggle-group">
                                  <div
                                    class="toggle-switch"
                                    :class="{ 'limit-wind-option': isRouteWindDisabled(element) }"
                                  >
                                    <p><span></span>航路上の風</p>
                                    <input
                                      type="checkbox"
                                      :id="`route-wind-${element.id}`"
                                      class="toggle-checkbox"
                                      v-model="element.route_layer"
                                      :disabled="isRouteWindDisabled(element)"
                                    />
                                    <label class="toggle-label" :for="`route-wind-${element.id}`" />
                                  </div>
                                  <div
                                    class="toggle-switch"
                                    :class="{ 'limit-wind-option': isUpperWindDisabled(element) }"
                                  >
                                    <p><span></span>上空の風</p>
                                    <input
                                      type="checkbox"
                                      :id="`upper-wind-${element.id}`"
                                      class="toggle-checkbox"
                                      v-model="element.upper_layer"
                                      :disabled="isUpperWindDisabled(element)"
                                    />
                                    <label class="toggle-label" :for="`upper-wind-${element.id}`" />
                                  </div>
                                </div>
                              </fieldset>
                            </li>
                          </ul>

                          <div class="delete-spot-all">
                            <button @click.prevent="removePoint(element.id)">
                              <span></span>地点を削除
                            </button>
                          </div>
                        </div>
                      </div>
                    </details>
                  </li>
                </template>
              </draggable>
              <div class="add-point" :class="{ 'limit-error': isPointLimit }">
                <button @click.prevent="addPoint"><span></span>地点を追加</button>
                <p v-if="isPointLimit">
                  登録可能な地点数の上限に達しました。<br />
                  これ以上の地点は登録できません。
                </p>
              </div>
              <ul class="legend-wind-mark">
                <li>
                  <img src="../assets/images/icon/icon_pin.svg" alt="航路上の風" />{{
                    routeWindCount
                  }}/{{ ROUTE_WIND_MAX }}
                </li>
                <li>
                  <img src="../assets/images/icon/icon_flag.svg" alt="上空の風" />{{
                    upperWindCount
                  }}/{{ UPPER_WIND_MAX }}
                </li>
              </ul>
            </div>

            <!-- ===== 地図 ===== -->
            <div class="route-points-map-view">
              <button type="button" class="button-secondary" @click="applyToMap">
                <span>
                  <img src="../assets/images/icon/icon_move.svg" alt="" />
                </span>
                変更を地図に反映する
              </button>

              <GsiMap
                mode="route"
                :points="mapPoints"
                :mapType="mapModalStore.mapType"
                @click:route-point="openEditModal"
              />

              <!-- 凡例（★地図の外） -->
              <ul class="legend-wind-color">
                <li><span></span>航路上の風</li>
                <li><span></span>上空の風</li>
              </ul>
            </div>

            <!-- ===== コメント ===== -->
            <div>
              <label class="label-l">コメント</label>
              <div class="config-memo">
                <TextareaCounter
                  v-model="route.comment"
                  name="comment"
                  rules="max_length:100"
                  :rows="3"
                  :limit="100"
                />
                <ErrorMessageBox name="comment" />
              </div>
            </div>
          </div>
        </div>

        <!-- ===== Action ===== -->
        <div class="config-action">
          <div class="action-button-group">
            <button
              class="button-secondary alert"
              @click="onClickUndo"
              type="button"
              id="undoButton"
            >
              <span>
                <img src="../assets/images/icon/icon_undo.svg" alt="" />
              </span>
              リセット
            </button>

            <button
              class="button-primary"
              id="saveButton"
              type="submit"
              :class="{
                'is-disabled':
                  !meta.valid ||
                  !isRouteRequired ||
                  !hasMinimumPoints ||
                  routeWindCount > ROUTE_WIND_MAX ||
                  upperWindCount > UPPER_WIND_MAX,
              }"
              :disabled="
                !meta.valid ||
                !isRouteRequired ||
                !hasMinimumPoints ||
                routeWindCount > ROUTE_WIND_MAX ||
                upperWindCount > UPPER_WIND_MAX
              "
            >
              設定を保存する
            </button>
          </div>
          <ErrorMessageBox
            v-if="serverErrorMessage"
            :message="serverErrorMessage"
            class="login-action-error-message"
          />

          <div class="delet-all">
            <button type="button" @click="handleDelete" style="display: inline-flex">
              <img src="../assets/images/icon/icon_delete.svg" alt="" />
              航路情報を削除
            </button>
          </div>
        </div>
      </Form>

      <!-- パンくず -->
      <div aria-label="Breadcrumb" class="breadcrumb-container-under">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <RouterLink to="/settings" custom v-slot="{ navigate }">
              <a href="#" @click.prevent="onClickBreadcrumb(navigate)"> 設定トップ </a>
            </RouterLink>
            <span class="breadcrumb-separator"></span>
          </li>
          <li class="breadcrumb-item">
            <RouterLink to="/flight-routes" custom v-slot="{ navigate }">
              <a href="#" @click.prevent="onClickBreadcrumb(navigate)"> 航路選択 </a>
            </RouterLink>
            <span class="breadcrumb-separator"></span>
          </li>
          <li class="breadcrumb-item">
            <span aria-current="page">航路情報設定</span>
          </li>
        </ol>
      </div>
      
    </div>
  </div>

  <MapModal
    :isOpen="mapModalStore.visible"
    :lat="mapModalStore.lat"
    :lon="mapModalStore.lon"
    :mapType="mapModalStore.mapType"
    mapContext="route-point"
    :markerLabel="mapModalStore.markerLabel"
    @cancel="mapModalStore.cancel()"
    @apply="onApplyFromModal"
  />
  <UndoAlertModal
    :isOpen="confirmModal.visible"
    :title="confirmModal.title"
    :message="confirmModal.message"
    :cancelText="confirmModal.cancelText"
    :confirmText="confirmModal.confirmText"
    @cancel="confirmModal.cancel()"
    @confirm="confirmModal.confirm()"
  />
  <Toast />
</template>

<script setup lang="ts">
/* =========================
 * import
 ========================= */
import { Form } from 'vee-validate'
import { computed, ref, onMounted, watch } from 'vue'
import { useRouter, useRoute, type NavigationFailure } from 'vue-router'
import draggable from 'vuedraggable'
import GsiMap from '@/components/map/GsiMap.vue'
import MapModal from '@/components/modal/MapModal.vue'
import UndoAlertModal from '@/components/modal/UndoAlertModal.vue'
import Toast from '@/components/common/Toast.vue'
import InputText from '@/components/form/InputText.vue'
import NumberField from '@/components/form/NumberField.vue'
import TextareaCounter from '@/components/form/TextareaCounter.vue'
import ErrorMessageBox from '@/components/form/ErrorMessageBox.vue'
import { MIN_LATITUDE, MAX_LATITUDE, MIN_LONGITUDE, MAX_LONGITUDE } from '@/constants'
import { fetchFlightRoute, updateFlightRoute, deleteFlightRoute } from '@/api/flightRoutesApi'
import { useFlightAreaStore } from '@/stores/flightArea'
import { useConfirmModalStore } from '@/stores/confirmModal'
import { useMapModalStore } from '@/stores/mapModal'
import { useToast } from '@/composables/useToast'
import { getFieldMessage } from '@/utils/message'
import { useConfirmBeforeLeave } from '@/composables/useConfirmBeforeLeave'
import { useConfirmDiscard } from '@/composables/useConfirmDiscard'

/* =========================
 * constants / types
 ========================= */
const MAX_POINT_COUNT = 30
const ROUTE_WIND_MAX = 10
const UPPER_WIND_MAX = 3

type UiPoint = {
  id: string
  point_name: string
  lat: number | null
  lon: number | null
  altitude: number
  speed: number
  route_layer: boolean
  upper_layer: boolean
  opened: boolean
}

/* =========================
 * router / store
 ========================= */
const router = useRouter()
const routeParams = useRoute()
const flightAreaStore = useFlightAreaStore()
const confirmModal = useConfirmModalStore()
const mapModalStore = useMapModalStore()
const { show } = useToast()

const areaId = Number(routeParams.params.areaId)
const routeId = Number(routeParams.params.routeId)

/* =========================
 * state
 ========================= */
const route = ref<{
  flight_route_name: string
  comment: string
  points: UiPoint[]
}>({
  flight_route_name: '',
  comment: '',
  points: [],
})

const mapPoints = ref<
  {
    lat: number
    lon: number
    label: string
    routeWind: boolean
    upperWind: boolean
  }[]
>([])

const touchedPoints = ref<Record<string, boolean>>({})
const editingPointIndex = ref<number | null>(null)
const editingPointId = ref<string | null>(null)
const serverErrorMessage = ref<string>('')

const originalSnapshot = ref<any | null>(null)
const isDirty = ref(false)

// 戻るボタン
useConfirmBeforeLeave(isDirty)

/* =========================
 * computed
 ========================= */
const routeWindCount = computed(() => route.value.points.filter((p) => p.route_layer).length)
const upperWindCount = computed(() => route.value.points.filter((p) => p.upper_layer).length)
const isPointLimit = computed(() => route.value.points.length >= MAX_POINT_COUNT)
const hasMinimumPoints = computed(() => route.value.points.length >= 2)

const isRouteRequired = computed(() => {
  if (route.value.flight_route_name.trim() !== '') return true

  return route.value.points.some(
    (p) =>
      !(
        p.point_name === '' &&
        p.lat === null &&
        p.lon === null &&
        p.altitude === 0 &&
        p.speed === 0
      ),
  )
})

/* =========================
 * lifecycle
 ========================= */
onMounted(async () => {
  loadFromDb()
})

/* =========================
 * watch
 ========================= */
watch(
  route,
  () => {
    isDirty.value = isChanged()
  },
  { deep: true },
)

/* =========================
 * db
 ========================= */
async function loadFromDb() {
  if (!routeId || Number.isNaN(routeId) || !areaId || Number.isNaN(areaId)) {
    router.push('/error')
    return
  }

  // エリア一覧を取得
  await flightAreaStore.fetchFlightAreas()
  const areaIds = flightAreaStore.flightAreas.map((a) => a.flight_area_id)
  if (0 == areaIds.length) {
    // 全エリアが削除された場合、飛行エリア設定確認画面へ遷移
    router.replace('/flight-area-empty')
    return
  } else if (!areaIds.includes(areaId)) {
    // 選択エリアが削除された場合、前画面へ遷移
    router.replace('/flight-routes')
    return
  }

  const detail = await fetchFlightRoute(routeId)

  // 取得できない場合、前画面へ戻る
  if (!detail) router.replace('/flight-routes')

  route.value.flight_route_name = detail.flight_route_name
  route.value.comment = detail.comment ?? ''

  route.value.points = (detail.points ?? [])
    .sort((a, b) => a.point_no - b.point_no)
    .map((p) => ({
      id: crypto.randomUUID(),
      point_name: p.point_name,
      lat: p.point_coordinate?.latitude ?? null,
      lon: p.point_coordinate?.longitude ?? null,
      altitude: p.point_height ?? 0,
      speed: p.point_passing_speed ?? 0,
      route_layer: p.route_point_profile_flg === 1,
      upper_layer: p.upper_point_profile_flg === 1,
      opened: false,
    }))

  applyToMap()
  originalSnapshot.value = normalizeRouteForCompare(route.value)
}

/* =========================
 * normalize / diff
 ========================= */
function normalizeRouteForCompare(value: {
  flight_route_name: string
  comment: string
  points: UiPoint[]
}) {
  return {
    flight_route_name: value.flight_route_name,
    comment: value.comment,
    points: value.points.map((p) => ({
      point_name: p.point_name,
      lat: p.lat,
      lon: p.lon,
      altitude: p.altitude,
      speed: p.speed,
      route_layer: p.route_layer,
      upper_layer: p.upper_layer,
    })),
  }
}

function isChanged(): boolean {
  if (!originalSnapshot.value) return false
  return (
    JSON.stringify(normalizeRouteForCompare(route.value)) !== JSON.stringify(originalSnapshot.value)
  )
}

/* =========================
 * ui helpers
 ========================= */
function togglePoint(p: UiPoint) {
  p.opened = !p.opened
}

function addPoint() {
  if (route.value.points.length >= MAX_POINT_COUNT) return

  const id = crypto.randomUUID()

  route.value.points.push({
    id,
    point_name: '',
    lat: null,
    lon: null,
    altitude: 0,
    speed: 0,
    route_layer: false,
    upper_layer: false,
    opened: true,
  })

  touchedPoints.value[id] = true
}

function removePoint(id: string) {
  route.value.points = route.value.points.filter((p) => p.id !== id)
}

/* =========================
 * validation helpers
 ========================= */
function isPointValidationActive(_: UiPoint, pointId: string): boolean {
  return !!touchedPoints.value[pointId]
}

function hasPointError(
  pointId: string,
  errors: Partial<Record<string, string | undefined>>,
): boolean {
  if (!touchedPoints.value[pointId]) return false
  return Object.keys(errors).some((key) => key.startsWith(`points.${pointId}.`))
}

function isRouteWindDisabled(p: UiPoint): boolean {
  if (p.route_layer) return false
  return routeWindCount.value >= ROUTE_WIND_MAX
}

function isUpperWindDisabled(p: UiPoint): boolean {
  if (p.upper_layer) return false
  return upperWindCount.value >= UPPER_WIND_MAX
}

/* =========================
 * map
 ========================= */
function applyToMap() {
  mapPoints.value = route.value.points
    .map((p, index) => ({ point: p, index }))
    .filter(({ point }) => point.lat != null && point.lon != null)
    .map(({ point, index }) => ({
      lat: point.lat!,
      lon: point.lon!,
      label: String(index + 1),
      routeWind: !!point.route_layer,
      upperWind: !!point.upper_layer,
    }))
}

function openMapDialog(point: UiPoint, index: number) {
  editingPointId.value = point.id
  mapModalStore
    .open({
      lat: point.lat,
      lon: point.lon,
      markerLabel: String(index + 1),
      routeWind: point.route_layer,
      upperWind: point.upper_layer,
    })
    .then((result) => {
      if (!result) return
      point.lat = result.lat
      point.lon = result.lon
    })
}

function onApplyFromModal({ lat, lon }: { lat: number; lon: number }) {
  const id = editingPointId.value
  if (!id) return

  const point = route.value.points.find((p) => p.id === id)
  if (!point) return

  point.lat = lat
  point.lon = lon
  editingPointId.value = null

  // 親地図の更新
  applyToMap()
}

function openEditModal(point: {
  index: number
  lat: number
  lon: number
  label: string
  routeWind?: boolean
  upperWind?: boolean
}) {
  const target = route.value.points[point.index]
  if (!target) return
  editingPointId.value = target.id

  editingPointIndex.value = point.index
  mapModalStore.open({
    lat: point.lat,
    lon: point.lon,
    markerLabel: point.label,
    routeWind: point.routeWind,
    upperWind: point.upperWind,
  })
}

/* =========================
 * actions
 ========================= */
async function handleSave() {
  if (!isRouteRequired.value) return
  if (!hasMinimumPoints.value) return
  if (routeWindCount.value > ROUTE_WIND_MAX) return
  if (upperWindCount.value > UPPER_WIND_MAX) return

  const payload = {
    flight_route_name: route.value.flight_route_name,
    comment: route.value.comment,
    points: route.value.points.map((p, index) => ({
      point_no: index + 1,
      point_name: p.point_name,
      point_height: p.altitude,
      point_passing_speed: p.speed,
      point_coordinate: {
        latitude: p.lat!,
        longitude: p.lon!,
      },
      route_point_profile_flg: p.route_layer ? 1 : 0,
      upper_point_profile_flg: p.upper_layer ? 1 : 0,
      point_port_cd: index === 0 ? 1 : index === route.value.points.length - 1 ? 2 : 3,
    })),
  }

  const response = await updateFlightRoute(routeId, payload)
  handleServerResponse(response, 'operationncompleted004')
}

function handleServerResponse(response: any, messageId: string) {
  const messageIds = response && 'message_ids' in response ? (response.message_ids ?? []) : []
  if (messageIds && messageIds.length > 0) {
    serverErrorMessage.value = getFieldMessage(messageIds)
  } else {
    loadFromDb()
    show(getFieldMessage(messageId))
  }
}

async function handleDelete() {
  const ok = await confirmModal.open({
    title: getFieldMessage('routediscard003_1'),
    message: getFieldMessage('routediscard003_2'),
    confirmText: '削除する',
    cancelText: 'キャンセル',
  })
  if (!ok) return

  serverErrorMessage.value = ''
  const response = await deleteFlightRoute(routeId)

  if ('message_ids' in response && response.message_ids?.length) {
    serverErrorMessage.value = getFieldMessage(response.message_ids)
    return
  }

  show(getFieldMessage('operationdeleted005'))
  // 遷移履歴を1つ削除 航路選択画面で戻るを2回押さないと戻れない対応
  router.go(-1)
}

/* =========================
 * navigation
 ========================= */
async function onClickBreadcrumb(navigate: () => void) {
  if (!isDirty.value) {
    navigate()
    return
  }

  await confirmDiscardThen(() => {
    isDirty.value = false
    navigate()
  })
}

async function onClickUndo() {
  await confirmDiscardThen(async () => {
    editingPointIndex.value = null
    editingPointId.value = null
    await loadFromDb()
  })
}

type DiscardActionResult = void | Promise<void | NavigationFailure | undefined>

async function confirmDiscardThen(action: () => DiscardActionResult) {
  const { confirmDiscard } = useConfirmDiscard()

  const ok = await confirmDiscard()
  if (ok) {
    await action()
  }
}
</script>
<style>
.route-point-item {
  list-style: none;
}

.route-point-item:not(.has-error) > .advanced-settings-details[open]:last-child .details-summary {
  background-color: var(--blueGray);
}

/* open でも通常色（has-error でない限り） */
.route-point-item:not(.has-error) > details[open] > summary {
  background-color: var(--blueGray);
}

.route-point-item.has-error > details > summary {
  background-color: var(--redbg);
  border: 1px solid var(--redbg);
}

/* Vue.Draggable × iOS Safari 最小対策（航路情報設定のみ） */
.route-points-list .sortable-ghost,
.route-points-list .sortable-chosen {
  width: 100% !important;
  max-width: 100% !important;
}

.route-point-item {
  touch-action: pan-y;
}
</style>
